import { useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const STORAGE_KEY = 'gg_admin_history_v1';
const MAX_ENTRIES = 20;

function readStack() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeStack(arr) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(arr.slice(-MAX_ENTRIES)));
  } catch {
    /* storage full or disabled — safe to ignore */
  }
}

/**
 * useAdminHistoryTracker — mount this once (at the AdminRoute layout).
 * It appends every admin route visit to a session-scoped history stack so that
 * useSmartBack can restore the user's actual previous admin location.
 * Consecutive duplicates are skipped so a re-render doesn't inflate history.
 */
export function useAdminHistoryTracker() {
  const { pathname, search } = useLocation();
  const skipNext = useRef(false);
  useEffect(() => {
    if (!pathname.startsWith('/admin/')) return;
    if (skipNext.current) { skipNext.current = false; return; }
    const stack = readStack();
    const full = pathname + (search || '');
    if (stack[stack.length - 1] === full) return;
    stack.push(full);
    writeStack(stack);
  }, [pathname, search]);
}

/**
 * useSmartBack(fallback)
 *
 * Returns { back } where `back()`:
 *   1. Pops the top of the admin history stack (current page).
 *   2. Peeks the new top → if it's a valid admin route AND different from current, navigates there.
 *   3. Otherwise navigates to the provided fallback (logical parent) — never dumps to Dashboard
 *      unless the caller says so.
 *
 * Also handles the case where the browser back button was already pressed —
 * we don't rely on `navigate(-1)` because we lose control of state preservation
 * boundaries. Sessionstorage stack is our source of truth.
 */
export function useSmartBack(fallback = '/admin/dashboard') {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const back = useCallback(() => {
    const stack = readStack();
    const current = pathname + (search || '');
    // Trim any trailing duplicates that equal current
    while (stack.length > 0 && stack[stack.length - 1] === current) stack.pop();
    const prev = stack[stack.length - 1];
    // Persist trimmed stack
    writeStack(stack);
    if (prev && prev.startsWith('/admin/') && prev !== current) {
      // Also pop the entry we're about to navigate to so it doesn't double-stack
      stack.pop();
      writeStack(stack);
      navigate(prev);
      return;
    }
    navigate(fallback);
  }, [navigate, pathname, search, fallback]);

  return { back };
}

export default useSmartBack;
